import ErrorBox from "@/components/Error/Box";
import Icon from "@/icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const SettingsTeamDiscovery = () => {
    const { teamId } = useParams<{ teamId: string }>();

    const discoveryState = useQuery({
        queryKey: ["team", teamId, "discovery"],
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/teams/${teamId}/discovery`);
            return response.json();
        },
    });

    const [error, setError] = useState<string | null>(null);
    const [buttonDisabled, setButtonDisabled] = useState(false);

    useEffect(() => {
        const isDiscoverable = discoveryState.data?.eligibility.description && discoveryState.data?.eligibility.applicationQuestions;
        setButtonDisabled(!isDiscoverable);
    }, [discoveryState.data]);

    if (discoveryState.isLoading) {
        return (
            <div className="flex flex-col w-full h-full items-center justify-center">
            </div>
        );
    }

    if (discoveryState.isError) {
        return <ErrorBox message={(discoveryState.error as Error).message} />;
    }

    const changeDiscoveryState = async () => {
        if (buttonDisabled) return;
        setButtonDisabled(true);
        try {
            const response = await fetch(`__SITE_BASE__/api/teams/${teamId}/discovery`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    state: !discoveryState.data?.state,
                }),
            });
            const json = await response.json();
            if (!json.success) throw new Error(json.message);
            discoveryState.refetch();
        } catch (error) {
            setError((error as Error).message);
        } finally {
            setButtonDisabled(false);
        }
    };

    const isDiscoverable = discoveryState.data?.eligibility.description && discoveryState.data?.eligibility.applicationQuestions;

    return (
        <div className="flex flex-col w-full sm:px-32 h-full mb-12">
            <div className="flex flex-col w-full h-full items-center text-center">
                <div className="text-brand-green">
                    <Icon name="users" size="128" />
                </div>
                <h1 className="text-gray-200 font-semibold text-lg">Discovery</h1>
                <h2 className="text-gray-300 font-semibold text-sm sm:px-32">
                    Find and connect with players who match your interests and play style. Explore the list below to discover new teammates and enhance your gaming experience.
                </h2>

                {error && (
                    <div className="bg-brand-profile w-full border-r-4 text-center mb-2 border-l-4 border-red-500 text-white mt-4 p-4" role="alert">
                        <p className="font-bold">An error occurred</p>
                        <p className="text-white">{error}</p>
                    </div>
                )}
            </div>

            <div className="flex flex-col w-full sm:px-32 h-full mt-6">
                <div className="flex flex-col w-full rounded-t-lg bg-brand-dark p-4">
                    <div className={`flex gap-2 items-center ${isDiscoverable ? "text-green-500" : "text-red-500"}`}>
                        <Icon name="shield" size="24" />
                        <h1 className="text-gray-300 font-semibold text-lg">
                            {isDiscoverable ? "Your Team can be made discoverable." : "Your Team currently does not meet the requirements to be discoverable."}
                        </h1>
                    </div>
                </div>
                <hr className="border-gray-700" />
                <div className="flex flex-col w-full rounded-b-lg bg-brand-darkest p-4 gap-4">
                    <div className={`flex gap-2 items-center ${discoveryState.data?.eligibility.description ? "text-green-500" : "text-red-500"}`}>
                        <Icon name={discoveryState.data?.eligibility.description ? "verified" : "x"} size="24" />
                        <h1 className="text-gray-300 font-semibold text-lg">Your team must have a description</h1>
                    </div>
                    <div className={`flex gap-2 items-center mt-2 ${discoveryState.data?.eligibility.applicationQuestions ? "text-green-500" : "text-red-500"}`}>
                        <Icon name={discoveryState.data?.eligibility.applicationQuestions ? "verified" : "x"} size="24" />
                        <h1 className="text-gray-300 font-semibold text-lg">
                            Your team needs to be open to application questions and applications
                        </h1>
                    </div>
                </div>
            </div>

            <div className="flex flex-col w-full h-full items-center text-center mt-8">
                <button
                    className="bg-brand-green text-center px-5 py-2 w-64 rounded-lg text-white font-semibold hover:bg-green-500 disabled:opacity-50"
                    disabled={buttonDisabled}
                    onClick={changeDiscoveryState}
                >
                    {discoveryState.data?.state ? "Disable Discovery" : "Enable Discovery"}
                </button>
            </div>
        </div>
    );
};
